import { useState, useEffect } from 'react';

/**
 * Custom hook to detect and respond to viewport size changes
 * Returns an object with viewport dimensions and device type booleans
 *
 * @returns {Object} viewport - Object containing viewport information
 * @returns {number} viewport.width - Current viewport width
 * @returns {number} viewport.height - Current viewport height
 * @returns {boolean} viewport.isMobile - True if viewport width < 768px
 * @returns {boolean} viewport.isTablet - True if viewport width >= 768px and < 1024px
 * @returns {boolean} viewport.isDesktop - True if viewport width >= 1024px
 * @returns {boolean} viewport.isSmallMobile - True if viewport width < 480px
 * @returns {string} viewport.deviceType - 'mobile', 'tablet', or 'desktop'
 * @returns {string} viewport.orientation - 'portrait' or 'landscape'
 */
export const useResponsive = () => {
  const [viewport, setViewport] = useState(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      width,
      height,
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      isSmallMobile: width < 480,
      deviceType: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop',
      orientation: height > width ? 'portrait' : 'landscape'
    };
  });

  useEffect(() => {
    let timeoutId = null;

    const handleResize = () => {
      // Debounce resize events for better performance
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;

        setViewport({
          width,
          height,
          isMobile: width < 768,
          isTablet: width >= 768 && width < 1024,
          isDesktop: width >= 1024,
          isSmallMobile: width < 480,
          deviceType: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop',
          orientation: height > width ? 'portrait' : 'landscape'
        });
      }, 150); // 150ms debounce
    };

    window.addEventListener('resize', handleResize);

    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', handleResize);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return viewport;
};

/**
 * Custom hook to check if viewport matches a specific media query
 *
 * @param {string} query - Media query string (e.g., '(min-width: 768px)')
 * @returns {boolean} matches - True if media query matches
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    const handler = (e) => setMatches(e.matches);

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    // Fallback for older browsers
    else {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
};

/**
 * Hook to get safe area insets for devices with notches
 *
 * @returns {Object} insets - Safe area insets
 */
export const useSafeArea = () => {
  const [insets, setInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });

  useEffect(() => {
    const computeInsets = () => {
      const style = getComputedStyle(document.documentElement);

      setInsets({
        top: parseInt(style.getPropertyValue('--safe-top') || '0'),
        bottom: parseInt(style.getPropertyValue('--safe-bottom') || '0'),
        left: parseInt(style.getPropertyValue('--safe-left') || '0'),
        right: parseInt(style.getPropertyValue('--safe-right') || '0')
      });
    };

    computeInsets();

    // Recompute on orientation change
    window.addEventListener('orientationchange', computeInsets);

    return () => window.removeEventListener('orientationchange', computeInsets);
  }, []);

  return insets;
};
